/**
 * Video Media Custom Element
 * Handles video playback with auto-play on selection and lazy loading
 */

if (!customElements.get('video-media')) {
  customElements.define('video-media', class VideoMedia extends HTMLElement {
    constructor() {
      super();
      this.video = null;
      this.poster = null;
      this.isLoaded = false;
      this.isPlaying = false;
      this.group = this.getAttribute('group') || 'default';
      this.host = this.getAttribute('host');
      this.autoplay = this.hasAttribute('autoplay');
      this.showPlayButton = !this.hasAttribute('show-play-button');
      this.nonInteractive = this.hasAttribute('non-interactive');
      this.lazyLoad = this.hasAttribute('data-lazy-load');
      
      this.handlePlay = this.handlePlay.bind(this);
      this.handlePause = this.handlePause.bind(this);
      this.handleLoadedData = this.handleLoadedData.bind(this);
      this.handleGalleryChange = this.handleGalleryChange.bind(this);
    }

    connectedCallback() {
      this.video = this.querySelector('video');
      this.poster = this.querySelector('img');
      
      console.log('🎥 [VideoMedia] Connected:', {
        hasVideo: !!this.video,
        hasPoster: !!this.poster,
        lazyLoad: this.lazyLoad,
        group: this.group,
        videoSrc: this.video ? this.video.dataset.videoSrc : 'no src'
      });
      
      // Set up the element styling
      this.style.position = 'relative';
      this.style.display = 'block';
      this.style.width = '100%';
      this.style.height = '100%';
      if (this.nonInteractive) {
        this.style.pointerEvents = 'none';
      }
      
      if (this.video) {
        // Ensure video has proper styling
        this.video.style.width = '100%';
        this.video.style.height = '100%';
        this.video.style.objectFit = 'cover';
        this.video.style.display = 'block';
        
        // Set up video attributes for lazy loading
        this.video.setAttribute('preload', 'none');
        this.video.setAttribute('muted', 'true');
        this.video.setAttribute('playsinline', '');
        this.video.setAttribute('webkit-playsinline', '');
        
        this.video.addEventListener('play', this.handlePlay);
        this.video.addEventListener('pause', this.handlePause);
        this.video.addEventListener('loadeddata', this.handleLoadedData);
        
        console.log('🎥 [VideoMedia] Video element configured for lazy loading');
      }
      
      // Listen for gallery changes to auto-play/pause
      this.setupGalleryIntegration();

      // Add play button overlay only when enabled
      if (this.showPlayButton) {
        this.addPlayButton();
      }

      // Autoplay when in viewport (muted required by browsers)
      if (this.autoplay && this.video) {
        this.setupAutoplay();
      }
    }

    disconnectedCallback() {
      if (this.video) {
        this.video.removeEventListener('play', this.handlePlay);
        this.video.removeEventListener('pause', this.handlePause);
        this.video.removeEventListener('loadeddata', this.handleLoadedData);
      }
      this.removeEventListener('gallery:change', this.handleGalleryChange);
    }

    setupGalleryIntegration() {
      // Listen for gallery navigation events
      document.addEventListener('gallery:change', this.handleGalleryChange);
      
      // Also listen for thumbnail clicks
      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-thumbnail-button]')) {
          const button = e.target.closest('[data-thumbnail-button]');
          const index = parseInt(button.dataset.index);
          this.handleGalleryChange({ detail: { index } });
        }
      });
    }

    handleGalleryChange(event) {
      const targetIndex = event.detail ? event.detail.index : null;
      const currentIndex = this.getCurrentGalleryIndex();
      
      console.log('🎥 [VideoMedia] Gallery change detected:', {
        targetIndex,
        currentIndex,
        isThisVideo: targetIndex === currentIndex
      });
      
      if (targetIndex === currentIndex) {
        // This video should be active - load and play
        this.loadAndPlay();
      } else {
        // This video should be paused
        this.pauseVideo();
      }
    }

    getCurrentGalleryIndex() {
      const galleryItem = this.closest('[data-product-image]');
      return galleryItem ? parseInt(galleryItem.dataset.index) : -1;
    }

    loadAndPlay() {
      console.log('🎥 [VideoMedia] Loading and playing video');
      
      if (!this.isLoaded) {
        this.loadVideo();
      }
      
      // Play after a short delay to ensure video is ready
      setTimeout(() => {
        if (this.video && !this.isPlaying) {
          this.video.play().catch(error => {
            console.warn('🎥 [VideoMedia] Auto-play failed:', error);
          });
        }
      }, 100);
    }

    pauseVideo() {
      console.log('🎥 [VideoMedia] Pausing video');
      if (this.video && !this.video.paused) {
        this.video.pause();
      }
    }

    loadVideo() {
      if (this.isLoaded || !this.video) return;
      
      console.log('🎥 [VideoMedia] Loading video source');
      
      const videoSrc = this.video.dataset.videoSrc;
      const source = this.video.querySelector('source');
      
      if (videoSrc && source) {
        source.src = videoSrc;
        this.video.load();
        this.isLoaded = true;
        console.log('🎥 [VideoMedia] Video source loaded');
      }
    }

    handleLoadedData() {
      console.log('🎥 [VideoMedia] Video data loaded');
      this.classList.add('loaded');
    }

    setupAutoplay() {
      if (!this.video) return;
      this.video.setAttribute('muted', '');
      this.video.setAttribute('playsinline', '');
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.loadAndPlay();
            } else {
              this.pauseVideo();
            }
          });
        },
        { rootMargin: '50px', threshold: 0.25 }
      );
      observer.observe(this);
    }

    addPlayButton() {
      // Remove existing play button if any
      const existingButton = this.querySelector('.video-media__play-button');
      if (existingButton) {
        existingButton.remove();
      }
      
      const playButton = document.createElement('div');
      playButton.className = 'video-media__play-button absolute inset-0 z-20 flex items-center justify-center bg-black bg-opacity-20 transition-opacity duration-300';
      playButton.innerHTML = `
        <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="30" cy="30" r="30" fill="white" fill-opacity="0.9"/>
          <path d="M42 30L24 18v24l18-12z" fill="black"/>
        </svg>
      `;
      
      // Add click handler to manually trigger play
      playButton.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        console.log('🎥 [VideoMedia] Manual play button clicked');
        this.loadAndPlay();
      });
      
      this.appendChild(playButton);
    }

    handlePlay() {
      this.isPlaying = true;
      this.classList.add('playing');
      
      // Hide poster and play button when playing
      if (this.poster) {
        this.poster.style.opacity = '0';
      }
      
      const playButton = this.querySelector('.video-media__play-button');
      if (playButton) {
        playButton.style.opacity = '0';
      }
      
      console.log('🎥 [VideoMedia] Video started playing');
    }

    handlePause() {
      this.isPlaying = false;
      this.classList.remove('playing');
      
      // Show poster and play button when paused
      if (this.poster) {
        this.poster.style.opacity = '1';
      }
      
      const playButton = this.querySelector('.video-media__play-button');
      if (playButton) {
        playButton.style.opacity = '1';
      }
      
      console.log('🎥 [VideoMedia] Video paused');
    }
  });
}
